import * as React from 'react'
import cx from 'classnames'
import { useAppDispatch } from 'reduxTyped'
import { Icon, palettes } from '@manychat/manyui'

import * as atypes from 'apps/chat/constants/LiveChatReduxActionTypes'
import { ImageBtn } from 'apps/chat/controllers/form/Toolbar/ImageBtn/ImageBtn'
import { SliceType } from 'components/MCTextEditor/slice/types'
import useDecorators from 'components/RichTextEditor/decorators/useDecorators'
import Editor, { IEditorRef, IEditorProps } from 'components/RichTextEditor/Editor'
import { EditorContextProvider } from 'components/RichTextEditor/EditorContext'
import { IProcessorState, PreloadImagesArray } from 'components/RichTextEditor/processor/constants'
import { isRangesEqual } from 'components/RichTextEditor/processor/range'
import { getSelection } from 'components/RichTextEditor/processor/selectors/textProcessorSelectors'
import { IEditorContext, InputElement } from 'components/RichTextEditor/types'
import useHistory from 'components/RichTextEditor/useHistory'
import { IMinimalChangeEventHandler } from 'constants/constants'
import { useDebouncedHandler, useDidMountEffect } from 'utils/commonHooks'

import { exportString, sortSlicesByType } from './dataFormat/exporter'
import { createParseString } from './dataFormat/parser'
import MCEditorContext from './MCEditorContext'
import OptionalScroll from './OptionalScroll'
import { useProcessorFabric } from './shared/hooks/useProcessorFabric'
import Slice from './slice/Slice'
import Toolbar from './toolbar/Toolbar'
import { AllowedValues } from './types'
import { withMCEditorContextProvider } from './withMCEditorContextProvider'

import cm from './MCTextEditor.module.css'

export interface EditorTheme {
  wrapper?: string
  focused?: string
  invalid?: string
  disabled?: string
  textInput?: string
  toolbar?: string
}

const noop = () => {
  return
}

interface IMCTextEditorProps
  extends Omit<
    IEditorProps,
    | 'createProcessor'
    | 'processor'
    | 'onChange'
    | 'onSelect'
    | 'exportString'
    | 'parseString'
    | 'decorators'
    | 'sliceComponent'
    | 'sortSlices'
  > {
  className?: string
  inputPadding?: number
  inputPaddingBottom?: number
  isValid?: boolean
  maxRows?: number
  minRows?: number
  noDelay?: boolean
  noLineBreaks?: boolean
  onChange?: IMinimalChangeEventHandler
  onPasteImage?: (file: File[]) => void
  theme?: EditorTheme
  value?: AllowedValues
  focusOnMount?: boolean
  withDefaultStyles?: boolean
  withSmartLinks?: boolean
  withVariables?: boolean
  ['data-onboarding-id']?: string
  preloadImages?: PreloadImagesArray
  preloadImagesClassName?: string
  imagesWidgetDisabled?: boolean
}

const ONCHANGE_DELAY = 300

export interface IMCTextEditorRef extends IEditorRef {
  select: (start?: number, end?: number) => void
  insert: (value: string, selectionOffset?: number) => void
}

const MCTextEditor: React.ForwardRefRenderFunction<IMCTextEditorRef, IMCTextEditorProps> = (
  {
    className,
    inputPadding = 7,
    inputPaddingBottom = 7,
    isValid,
    maxRows,
    minRows,
    noDelay,
    noLineBreaks,
    onBlur,
    onChange = noop,
    onFocus,
    onPasteImage,
    focusOnMount = true,
    theme,
    value,
    withDefaultStyles,
    withSmartLinks,
    withVariables,
    preloadImages = [],
    preloadImagesClassName,
    placeholder: placeholderString,
    imagesWidgetDisabled,
    ...editorProps
  },
  ref,
) => {
  const MAX_UPLOAD_IMAGES_COUNT = 5
  const dispatch = useAppDispatch()
  const valueRef = React.useRef<AllowedValues>('')
  const wrapperRef = React.useRef<HTMLDivElement>(null)
  const editorRef = React.useRef<IEditorRef>({} as IEditorRef)

  const [isFocused, setIsFocused] = React.useState(false)
  const context = React.useContext(MCEditorContext)
  const onChangeDebounced = useDebouncedHandler(onChange, ONCHANGE_DELAY)

  const disabled = editorProps.disabled ?? false

  const parseString = React.useMemo(() => {
    return createParseString(context)
  }, [context])

  const createProcessor = useProcessorFabric({
    withVariables,
    withSmartLinks,
    noLineBreaks: noLineBreaks || editorProps.multiline === false,
  })

  const [processorState, setProcessorState] = React.useState<IProcessorState>(
    createProcessor().getState(),
  )

  useDidMountEffect(() => {
    if (focusOnMount) editorRef.current?.focus()
  })

  const isValueChanged = valueRef.current !== value
  valueRef.current = value
  const isInvalidState = isValueChanged && exportString(processorState) !== value
  const processor = createProcessor(isInvalidState ? undefined : processorState)
  if (isInvalidState) {
    const parseResult = parseString(value)
    processor.insert(0, parseResult.text, parseResult.slices)
    setProcessorState(processor.getState())
  } else {
    processor.render()
  }

  const handleChange = (processorState: IProcessorState) => {
    setProcessorState(processorState)
    const value = exportString(processorState)
    const event = { target: { value, name: editorProps.name } }
    noDelay ? onChange(event) : onChangeDebounced(event)
  }

  const handleDeleteImg = (index: number) =>
    dispatch({ type: atypes.THREADS_DELETE_PRELOAD_IMAGE, data: { index } })

  const [edit, undo, redo] = useHistory<IProcessorState>((item) => {
    const isFocused = document.activeElement === editorRef.current?.inputRef?.current
    if (!isFocused && !isRangesEqual(processor.getSelection(), getSelection(item))) {
      editorRef.current?.focus()
    }
    handleChange(item)
  }, processor.getState())

  const handleInsert = (
    value: string,
    selectionOffset?: number,
    options = { shouldFocus: true },
  ) => {
    if (options.shouldFocus) {
      editorRef.current?.focus()
    }

    const selection = processor.getSelection()

    const parseResult = parseString(value)
    if (selection.length) {
      processor.delete(selection.start, selection.length)
    }
    processor.insert(selection.start, parseResult.text, parseResult.slices)

    if (typeof selectionOffset !== 'undefined') {
      processor.select(selection.start + selectionOffset)
    }
    edit(processor.getState())
  }

  const handleSelect = (start?: number, end?: number) => {
    if (typeof start === 'undefined') {
      start = 0
      end = processor.getText().length
    }
    if (typeof end === 'undefined') {
      end = start
    }

    processor.select(start, end)
    edit(processor.getState())

    editorRef.current?.focus()
  }

  const handleFocus = (event: React.FocusEvent<InputElement>) => {
    setIsFocused(true)
    onFocus && onFocus(event)
  }

  const handleBlur = (event: React.FocusEvent<InputElement>) => {
    setIsFocused(false)
    onBlur && onBlur(event)
  }

  const decoratorsTypes = [
    SliceType.BOLD,
    SliceType.ITALIC,
    SliceType.UNDERLINE,
    SliceType.TEXT_COLOR,
  ]
  const decorators = useDecorators(processor, edit, (s) =>
    decoratorsTypes.includes(s.type as SliceType),
  )

  const placeholder =
    editorProps.useContentEditable && placeholderString ? (
      <span style={{ color: palettes.typography.disabledFG }}>{placeholderString}</span>
    ) : (
      placeholderString
    )

  const editorContext: IEditorContext = {
    input: editorRef.current?.inputRef?.current || null,
    processor,
    edit,
    undo,
    redo,
    insert: handleInsert,
  }

  React.useImperativeHandle(ref, () => ({
    ...editorRef?.current,
    select: handleSelect,
    insert: handleInsert,
  }))

  const withToolbar = !editorProps.readonly && !disabled
  const hasPreloadImages = preloadImages.length > 0
  const disabledAddImg = preloadImages.length >= MAX_UPLOAD_IMAGES_COUNT || imagesWidgetDisabled

  return (
    <div
      className={cx(
        theme?.wrapper,
        withDefaultStyles && cm.wrapper,
        isFocused && cx(theme?.focused, cm.focused),
        !isValid && cx(theme?.invalid, cm.invalid),
        disabled && cx(theme?.disabled, cm.disabled),
        'p-relative',
        className,
      )}
      data-onboarding-id={editorProps['data-onboarding-id']}
      ref={wrapperRef}
    >
      <EditorContextProvider value={editorContext}>
        <OptionalScroll
          inputPadding={inputPadding}
          inputPaddingBottom={inputPaddingBottom}
          multiline={editorProps.multiline}
          maxRows={maxRows}
        >
          <Editor
            ref={editorRef}
            className={theme?.textInput}
            processor={processor}
            createProcessor={createProcessor}
            decorators={decorators}
            exportString={exportString}
            parseString={parseString}
            onBlur={handleBlur}
            onFocus={handleFocus}
            rows={minRows}
            placeholder={placeholder}
            sliceComponent={Slice}
            sortSlices={sortSlicesByType}
            {...editorProps}
          />
        </OptionalScroll>
        {hasPreloadImages && (
          <div
            className={cx('d-flex flex-wrap align-center justify-start', preloadImagesClassName)}
          >
            {preloadImages.map(({ url, name }, i) => (
              <div
                className={cx(cm.wrapperImg, 'd-flex bg-white rounded c-pointer', {
                  ['m-l-xs']: i,
                  [`${cm.disabled} c-not-allowed`]: disabled || imagesWidgetDisabled,
                })}
                key={i}
              >
                <div
                  className={cx(cm.image, 'b-a rounded')}
                  style={{ backgroundImage: `url(${url})` }}
                  title={name}
                >
                  <div className={cx(cm.preloadHover, 'p-absolute justify-end b-a rounded')}>
                    {!disabled && !imagesWidgetDisabled && (
                      <Icon.Plus size={16} onClick={() => handleDeleteImg(i)} />
                    )}
                  </div>
                </div>
              </div>
            ))}
            <ImageBtn
              onSelect={onPasteImage}
              iconType="Plus"
              disabled={disabledAddImg || disabled}
            />
          </div>
        )}
        {withToolbar && (
          <Toolbar
            className={theme?.toolbar}
            decorators={decorators}
            editorRef={editorRef}
            onInsert={handleInsert}
            open={isFocused}
            wrapperRef={wrapperRef}
          />
        )}
      </EditorContextProvider>
    </div>
  )
}

export default withMCEditorContextProvider<IMCTextEditorRef, IMCTextEditorProps>(
  React.forwardRef<IMCTextEditorRef, IMCTextEditorProps>(MCTextEditor),
)

export const EditorWithoutContext = React.forwardRef<IMCTextEditorRef, IMCTextEditorProps>(
  MCTextEditor,
)
